
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Action } from 'vuex-class';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { Validate } from '@/types';
import { toErrorMessage } from '@/utils/errorHandling';
import Toast from '@/utils/toast';
import InfoBanner from '@/components/shared/suites/InfoBanner.vue';
import StandardModal from '@/components/shared/suites/StandardModal.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import TextField from '@/components/shared/TextField.vue';


@Component<ItemDiscountModal>({
	components: {
		InfoBanner,
		StandardModal,
		TextButton,
		TextField,
		ValidationObserver,
		ValidationProvider
	}
})
export default class ItemDiscountModal extends Vue {
	@Prop({ type: Object, required: true }) private item!: MenuOrderItem;
	@Prop({ type: Boolean, required: true }) private showModal!: boolean;
	@Action('applyItemDiscount', { namespace: 'orders' }) private applyItemDiscount!: (payload: { item: MenuOrderItem, percentageValue: number | null }) => Promise<void>;

	$refs!: { observer: Validate };

	private discountPercentage: number | null = null;
	private isApplyingDiscount: boolean = false;
	private isRemovingDiscount: boolean = false;

	/**
	 * Validate then apply the entered discount percentage, closing the modal on success.
	 *
	 * @return {Promise<void>}
	 */
	private async applyDiscount(): Promise<void> {
		if (!(await this.$refs.observer.validate())) {
			return;
		}
		try {
			this.isApplyingDiscount = true;
			await this.applyItemDiscount({ item: this.item, percentageValue: this.discountPercentage });
			this.$emit('close');
		}
		catch (error) {
			Toast.error(this.$t('orders.item_discount_modal.error', { errorMessage: toErrorMessage(error) }));
		}
		finally {
			this.isApplyingDiscount = false;
		}
	}

	/**
	 * Remove the applied discount.
	 * Bypasses validation since a null amount is the removal signal.
	 *
	 * @return {Promise<void>}
	 */
	private async removeDiscount(): Promise<void> {
		try {
			this.isRemovingDiscount = true;
			this.discountPercentage = null;
			await this.applyItemDiscount({ item: this.item, percentageValue: null });
		}
		catch (error) {
			Toast.error(this.$t('orders.item_discount_modal.error', { errorMessage: toErrorMessage(error) }));
		}
		finally {
			this.isRemovingDiscount = false;
		}
	}
}
